import React, { useState, useEffect } from 'react';
import { getReunionTypeShort, getReunionTypeLabel } from '../utils/reunionTypes';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useOrganisme } from '../context/OrganismeContext';
import {
  ArrowLeftIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';

export default function ModifierBureau() {
  const navigate = useNavigate();
  const { organismeActif } = useOrganisme();
  
  const [membresActuels, setMembresActuels] = useState([]);
  const [eligibles, setEligibles] = useState([]);
  const [reunions, setReunions] = useState([]);
  const [fonctions, setFonctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  
  // Modifications en cours
  const [retraits, setRetraits] = useState([]);
  const [ajouts, setAjouts] = useState([]);
  const [modifications, setModifications] = useState([]);
  
  // Modal ajout/modification
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAdherent, setSelectedAdherent] = useState(null);
  const [editingMembre, setEditingMembre] = useState(null);
  const [modalForm, setModalForm] = useState({
    fonction: '',
    reunionElectionBureauId: '',
  });

  // Modal retrait
  const [showRetraitModal, setShowRetraitModal] = useState(false);
  const [membreARetirer, setMembreARetirer] = useState(null);
  const [retraitForm, setRetraitForm] = useState({
    raisonRetrait: '',
    raisonRetraitAutre: '',
    reunionRetraitId: '',
    dateRetrait: '',
  });

  // Modal confirmation
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [generatePdf, setGeneratePdf] = useState(false);

  const titreConseil = organismeActif === 'SAR' ? 'Bureau du Conseil syndical' : 'Bureau du Conseil d\'administration';

  useEffect(() => {
    if (organismeActif) {
      fetchData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organismeActif]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const [compositionRes, eligiblesRes, reunionsRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/conseil/${organismeActif}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${process.env.REACT_APP_API_URL}/conseil/${organismeActif}/eligibles`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${process.env.REACT_APP_API_URL}/conseil/${organismeActif}/reunions`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
      ]);

      // Charger les membres actifs du bureau
      let membresActifs = compositionRes.data.actifs.filter(m => m.type === 'bureau');
      
      // Charger aussi les membres inactifs du bureau pour le filtrage
      let membresInactifs = compositionRes.data.inactifs?.filter(m => m.type === 'bureau') || [];
      
      // Combiner actifs et inactifs pour le filtrage
      let tousMembres = [...membresActifs, ...membresInactifs];

      setMembresActuels(tousMembres);
      setEligibles(eligiblesRes.data);
      setReunions(reunionsRes.data);
      setFonctions(compositionRes.data.fonctions);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAddModal = (fonction = '') => {
    setSelectedAdherent(null);
    setSearchTerm('');
    setModalForm({
      fonction,
      reunionElectionBureauId: '',
    });
    setEditingMembre(null);
    setShowModal(true);
  };

  const handleOpenEditModal = (membre) => {
    setSelectedAdherent(membre.adherent);
    setSearchTerm('');
    setModalForm({
      fonction: membre.fonction || '',
      reunionElectionBureauId: membre.reunionElection?._id || '',
    });
    setEditingMembre(membre);
    setShowModal(true);
  };

  const handleConfirmModal = () => {
    if (!selectedAdherent) {
      alert('Veuillez sélectionner un adhérent');
      return;
    }

    if (!modalForm.fonction) {
      alert('Veuillez sélectionner une fonction');
      return;
    }

    if (!modalForm.reunionElectionBureauId) {
      alert('Veuillez sélectionner une réunion d\'élection');
      return;
    }

    if (editingMembre) {
      // Modification
      if (editingMembre.adherent._id === selectedAdherent._id) {
        // Même adhérent : mise à jour directe
        const updatedMembres = membresActuels.map(m => {
          if (m._id === editingMembre._id) {
            return {
              ...m,
              bureau: {
                ...m.bureau,
                fonction: modalForm.fonction,
                reunionElection: reunions.find(r => r._id === modalForm.reunionElectionBureauId),
              },
            };
          }
          return m;
        });
        setMembresActuels(updatedMembres);

        const modifIndex = modifications.findIndex(mod => mod.membreId === editingMembre._id);
        if (modifIndex >= 0) {
          const updatedModifications = [...modifications];
          updatedModifications[modifIndex] = {
            membreId: editingMembre._id,
            fonction: modalForm.fonction,
            reunionElectionBureauId: modalForm.reunionElectionBureauId,
          };
          setModifications(updatedModifications);
        } else {
          setModifications([...modifications, {
            membreId: editingMembre._id,
            fonction: modalForm.fonction,
            reunionElectionBureauId: modalForm.reunionElectionBureauId,
          }]);
        }
      } else {
        // Adhérent différent : retrait + ajout
        setRetraits([...retraits, {
          membreId: editingMembre._id,
          raisonRetrait: 'modification',
          raisonRetraitAutre: 'Modification',
          reunionRetraitId: '',
        }]);

        setAjouts([...ajouts, {
          adherentId: selectedAdherent._id,
          adherentNom: `${selectedAdherent.prenom} ${selectedAdherent.nom}`,
          estBureau: true,
          fonction: modalForm.fonction,
          reunionElectionBureauId: modalForm.reunionElectionBureauId,
          dateCooptation: null,
        }]);
      }
    } else {
      // Ajout
      setAjouts([...ajouts, {
        adherentId: selectedAdherent._id,
        adherentNom: `${selectedAdherent.prenom} ${selectedAdherent.nom}`,
        estBureau: true,
        fonction: modalForm.fonction,
        reunionElectionBureauId: modalForm.reunionElectionBureauId,
        dateCooptation: null,
      }]);
    }

    setShowModal(false);
    setEditingMembre(null);
  };

  const handleOpenRetraitModal = (membre) => {
    setMembreARetirer(membre);
    setRetraitForm({
      raisonRetrait: '',
      raisonRetraitAutre: '',
      reunionRetraitId: '',
      dateRetrait: '',
    });
    setShowRetraitModal(true);
  };

  const handleConfirmRetrait = () => {
    if (!retraitForm.raisonRetrait) {
      alert('Veuillez sélectionner une raison de retrait');
      return;
    }

    if (retraitForm.raisonRetrait === 'autre' && !retraitForm.raisonRetraitAutre) {
      alert('Veuillez préciser la raison du retrait');
      return;
    }

    setRetraits([...retraits, {
      membreId: membreARetirer._id,
      raisonRetrait: retraitForm.raisonRetrait,
      raisonRetraitAutre: retraitForm.raisonRetraitAutre,
      reunionRetraitId: retraitForm.reunionRetraitId,
      retirerDuBureau: true,   // Retirer uniquement du bureau
      retirerDuConseil: false,  // Ne PAS retirer du conseil
    }]);

    setShowRetraitModal(false);
    setMembreARetirer(null);
  };

  const handleCancelRetrait = (membreId) => {
    setRetraits(retraits.filter(r => r.membreId !== membreId));
  };

  const handleSave = () => {
    if (retraits.length === 0 && ajouts.length === 0 && modifications.length === 0) {
      alert('Aucune modification à enregistrer');
      return;
    }
    setShowConfirmModal(true);
  };

  const handleConfirmSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      console.log('Envoi des données:', { ajouts, retraits, modifications, generatePdf });
      
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/conseil/${organismeActif}/masse`,
        { ajouts, retraits, modifications, generatePdf },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log('Réponse du serveur:', response.data);
      
      if (response.data.erreurs && response.data.erreurs.length > 0) {
        console.error('Erreurs lors de l\'enregistrement:', response.data.erreurs);
        alert(`Enregistrement partiel. Erreurs:\n${response.data.erreurs.map(e => `- ${e.erreur}`).join('\n')}`);
      } else {
        alert('Modifications enregistrées avec succès');
      }

      setShowConfirmModal(false);
      navigate(`/admin/composition-conseil`);
    } catch (err) {
      console.error('Erreur complète:', err);
      console.error('Réponse erreur:', err.response?.data);
      alert(err.response?.data?.message || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const filteredEligibles = eligibles.filter(e => {
    const nom = `${e.prenom} ${e.nom}`.toLowerCase();
    const matchSearch = nom.includes(searchTerm.toLowerCase());
    const notAlreadyAdded = !ajouts.some(a => a.adherentId === e._id);
    
    // Vérifier si l'adhérent est déjà ACTIF au bureau
    const membreBureau = membresActuels.find(m => {
      const adherentId = m.adherent?._id || m.adherent;
      return adherentId?.toString() === e._id?.toString() && m.actif === true;
    });
    
    // Inclure si : pas actif au bureau OU marqué pour retrait du bureau
    const disponible = !membreBureau || retraits.some(r => r.membreId === membreBureau._id);
    
    return matchSearch && notAlreadyAdded && disponible;
  });

  const isMarkedForRetrait = (membreId) => retraits.some(r => r.membreId === membreId);

  // Générer toutes les positions du bureau (6 ou 7 fonctions selon organisme)
  const generateAllPositionsBureau = () => {
    const fonctionsValues = (organismeActif === 'ADAR' || organismeActif === 'SAR')
      ? [
          'president',
          'vice_president',
          'vice_president_2',
          'secretaire',
          'secretaire_adjoint',
          'tresorier',
          'tresorier_adjoint'
        ]
      : [
          'president',
          'vice_president',
          'secretaire',
          'secretaire_adjoint',
          'tresorier',
          'tresorier_adjoint'
        ];
    
    return fonctionsValues.map(fonction => {
      const membre = membresActuels.find(m => m.fonction === fonction);
      const ajoutEnCours = ajouts.find(a => a.fonction === fonction);
      return {
        fonction,
        membre: membre || null,
        ajoutEnCours: ajoutEnCours || null,
      };
    });
  };

  const positionsBureau = generateAllPositionsBureau();

  const getFonctionLabel = (value) => {
    return fonctions.find(f => f.value === value)?.label || value;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin/composition-conseil')}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Modifier la composition du bureau
                </h1>
                <p className="text-sm text-gray-500 mt-1">
                  {organismeActif} - {titreConseil}
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => navigate('/admin/composition-conseil')}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleSave}
                disabled={saving || (retraits.length === 0 && ajouts.length === 0 && modifications.length === 0)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 flex items-center gap-2"
              >
                <CheckIcon className="h-5 w-5" />
                {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error}</p>
          </div>
        </div>
      )}

      <div className="px-8 py-6">
        {/* Tableau du bureau */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Membres du Bureau
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Cliquez sur une ligne pour ajouter ou modifier un membre
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fonction</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Adhérent</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Début</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {positionsBureau.map((position) => {
                  const membre = position.membre;
                  const ajoutEnCours = position.ajoutEnCours;
                  const isVacant = !membre && !ajoutEnCours;
                  const markedForRetrait = membre && isMarkedForRetrait(membre._id);

                  return (
                    <tr 
                      key={position.fonction} 
                      className={`${isVacant ? 'bg-gray-50 hover:bg-gray-100 cursor-pointer' : markedForRetrait ? 'bg-red-50' : 'hover:bg-gray-50'}`}
                      onClick={() => isVacant && handleOpenAddModal(position.fonction)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {getFonctionLabel(position.fonction)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {isVacant ? (
                          <span className="text-gray-400 italic">Vacant</span>
                        ) : ajoutEnCours ? (
                          <span className="text-green-700">{ajoutEnCours.adherentNom} <span className="text-xs">(à ajouter)</span></span>
                        ) : (
                          <span className={markedForRetrait ? 'text-red-700 line-through' : 'text-gray-900'}>
                            {membre.adherent?.prenom} {membre.adherent?.nom?.toUpperCase()}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {isVacant ? '-' : (
                          membre?.reunionElectionBureau?.date ? (
                            <span>
                              {getReunionTypeShort(membre.reunionElectionBureau.type, organismeActif)}{' '}
                              {new Date(membre.reunionElectionBureau.date).toLocaleDateString('fr-FR')}
                            </span>
                          ) : '-'
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {isVacant ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenAddModal(position.fonction);
                            }}
                            className="text-green-600 hover:text-green-800 font-medium"
                          >
                            Ajouter
                          </button>
                        ) : (
                          <div className="flex items-center gap-2">
                            {markedForRetrait ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCancelRetrait(membre._id);
                                }}
                                className="text-gray-600 hover:text-gray-800"
                              >
                                Annuler retrait
                              </button>
                            ) : (
                              <>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenEditModal(membre);
                                  }}
                                  className="text-indigo-600 hover:text-indigo-800"
                                >
                                  Modifier
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenRetraitModal(membre);
                                  }}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  Retirer
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal Retrait */}
      {showRetraitModal && membreARetirer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Retirer {membreARetirer.adherent?.prenom} {membreARetirer.adherent?.nom}
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Raison du retrait *
                </label>
                <select
                  value={retraitForm.raisonRetrait}
                  onChange={(e) => setRetraitForm({ ...retraitForm, raisonRetrait: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Sélectionner une raison</option>
                  <option value="demission">Démission</option>
                  <option value="non_adhesion">Non-adhésion</option>
                  <option value="fin_mandat">Fin de mandat</option>
                  <option value="autre">Autre (préciser)</option>
                </select>
              </div>

              {retraitForm.raisonRetrait === 'autre' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Précisez *
                  </label>
                  <input
                    type="text"
                    value={retraitForm.raisonRetraitAutre}
                    onChange={(e) => setRetraitForm({ ...retraitForm, raisonRetraitAutre: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Raison du retrait"
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Réunion du retrait
                  </label>
                  <select
                    value={retraitForm.reunionRetraitId}
                    onChange={(e) => setRetraitForm({ ...retraitForm, reunionRetraitId: e.target.value, dateRetrait: '' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                    disabled={!!retraitForm.dateRetrait}
                  >
                    <option value="">Aucune</option>
                    {reunions.map((r) => (
                      <option key={r._id} value={r._id}>
                        {new Date(r.date).toLocaleDateString('fr-FR')} - {getReunionTypeShort(r.type, organismeActif)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    OU Date de retrait
                  </label>
                  <input
                    type="date"
                    value={retraitForm.dateRetrait}
                    onChange={(e) => setRetraitForm({ ...retraitForm, dateRetrait: e.target.value, reunionRetraitId: '' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                    disabled={!!retraitForm.reunionRetraitId}
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Choisissez soit une réunion, soit une date de retrait
              </p>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowRetraitModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleConfirmRetrait}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Confirmer le retrait
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Confirmation Enregistrement */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Composition finale
            </h3>
            
            <p className="text-gray-600 mb-4">
              Vous êtes sur le point d'enregistrer les modifications de la composition du bureau.
            </p>

            <div className="mb-6">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={generatePdf}
                  onChange={(e) => setGeneratePdf(e.target.checked)}
                  className="h-5 w-5 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Enregistrer la composition en PDF
                </span>
              </label>
              <p className="ml-8 mt-1 text-xs text-gray-500">
                Le PDF sera disponible dans la section "Documents de composition"
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  setGeneratePdf(false);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                disabled={saving}
              >
                Annuler
              </button>
              <button
                onClick={handleConfirmSave}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                disabled={saving}
              >
                {saving ? 'Enregistrement...' : 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Ajout/Modification */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {editingMembre ? 'Modifier un membre du bureau' : 'Ajouter un membre au bureau'}
            </h3>

            <div className="space-y-4">
              {/* Recherche d'adhérent */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {editingMembre ? 'Adhérent' : 'Rechercher un adhérent *'}
                </label>
                
                {editingMembre ? (
                  <div className="p-3 bg-gray-100 border border-gray-300 rounded-lg">
                    <span className="font-medium text-gray-700">
                      {selectedAdherent.prenom} {selectedAdherent.nom}
                    </span>
                    <p className="text-xs text-gray-500 mt-1">
                      Pour changer l'adhérent, veuillez d'abord retirer ce membre puis en ajouter un nouveau
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="relative">
                      <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="Nom ou prénom..."
                      />
                    </div>
                    
                    {searchTerm && (
                      <div className="mt-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg">
                        {filteredEligibles.slice(0, 10).map((e) => (
                          <button
                            key={e._id}
                            onClick={() => {
                              setSelectedAdherent(e);
                              setSearchTerm('');
                            }}
                            className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-0"
                          >
                            {e.prenom} {e.nom}
                          </button>
                        ))}
                        {filteredEligibles.length === 0 && (
                          <p className="px-3 py-2 text-gray-500 text-sm">Aucun adhérent trouvé</p>
                        )}
                      </div>
                    )}

                    {selectedAdherent && (
                      <div className="mt-2 flex items-center justify-between p-2 bg-indigo-50 border border-indigo-200 rounded-lg">
                        <span className="font-medium text-indigo-800">
                          {selectedAdherent.prenom} {selectedAdherent.nom}
                        </span>
                        <button
                          onClick={() => setSelectedAdherent(null)}
                          className="text-indigo-600 hover:text-indigo-800"
                        >
                          <XMarkIcon className="h-5 w-5" />
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Fonction */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fonction *
                </label>
                <select
                  value={modalForm.fonction}
                  onChange={(e) => setModalForm({ ...modalForm, fonction: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Sélectionner une fonction</option>
                  {fonctions.map((f) => {
                    // Vérifier si la fonction est déjà occupée
                    const estOccupee = membresActuels.some(m => m.fonction === f.value && m._id !== editingMembre?._id) ||
                                       ajouts.some(a => a.fonction === f.value);
                    
                    return (
                      <option 
                        key={f.value} 
                        value={f.value}
                        disabled={estOccupee}
                        className={estOccupee ? 'text-gray-400' : ''}
                      >
                        {f.label}{estOccupee ? ' (déjà occupé)' : ''}
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* Réunion d'élection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Réunion d'élection *
                </label>
                <select
                  value={modalForm.reunionElectionBureauId}
                  onChange={(e) => setModalForm({ ...modalForm, reunionElectionBureauId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Sélectionner une réunion</option>
                  {reunions.map((r) => (
                    <option key={r._id} value={r._id}>
                      {new Date(r.date).toLocaleDateString('fr-FR')} - {getReunionTypeLabel(r.type, organismeActif)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleConfirmModal}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                {editingMembre ? 'Modifier' : 'Ajouter'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
